import React, { useCallback } from 'react';
import { UploadTwoTone } from '@mui/icons-material';
import { ButtonBase, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { notify } from '../utils/notification';
import { FullSizeLoader } from './FullSizeLoader';

export const ALLOWED_FILES = [
  { name: 'pdf', mime: ['application/pdf'] },
  { name: 'png', mime: ['image/png'] },
  { name: 'jpeg', mime: ['image/jpeg'] },
  { name: 'tiff', mime: ['image/tiff'] },
  {
    name: 'xlsx',
    mime: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  },
  {
    name: 'docx',
    mime: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  { name: 'xml', mime: ['application/xml', 'text/xml'] },
];

export const SUPPORTED_MIME_TYPES = ALLOWED_FILES.map((file) => file.mime).flat();
export const SUPPORTED_MIME_TYPES_FOR_MASS_UPLOAD = ALLOWED_FILES.filter((file) => file.name !== 'xml')
  .map((file) => file.mime)
  .flat();

export const SUPPORTED_MIME_TYPES_FOR_BATCH_UPLOAD = ALLOWED_FILES.filter(
  (file) => file.name === 'xml' || file.name === 'xlsx',
)
  .map((file) => file.mime)
  .flat();

interface Props {
  onFileUpload: (files: File[]) => void;
  loading?: boolean;
}

export const FileDropzone: React.FC<Props> = ({ onFileUpload, loading }) => {
  const { t } = useTranslation('page-upload');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFileUpload(acceptedFiles);
    },
    [onFileUpload],
  );

  const { getRootProps, getInputProps, isDragReject, isDragActive } = useDropzone({
    onDropAccepted: onDrop,
    onDropRejected: () => notify.negative({ message: t('dropError') }),
    multiple: true,
  });

  return (
    <ButtonBase
      {...getRootProps()}
      sx={{
        maxWidth: 1024,

        height: '100%',
        width: '100%',
        border: 1,
        borderRadius: 1,
        borderStyle: 'solid',
        borderColor: isDragReject ? 'error.main' : isDragActive ? 'primary.main' : 'secondary.100',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        p: 4,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {loading ? <FullSizeLoader text={t('uploadLoading')} /> : null}
      <input {...getInputProps()} />
      <UploadTwoTone
        color={isDragReject ? 'error' : isDragActive ? 'primary' : 'action'}
        sx={{
          width: 72,
          height: 72,
        }}
      />
      <Typography
        variant={'body2'}
        sx={{
          whiteSpace: 'break-spaces',
        }}
      >
        {t('dropInstruction')}
      </Typography>
    </ButtonBase>
  );
};
